import { useState, useEffect } from 'react';
import { X, Database, Columns, Plus, Trash2, Search, Calendar, Tag, Edit2 } from 'lucide-react';
import { domainApi } from '../../utils/api';
import RenameModal from './RenameModal';
import toast from 'react-hot-toast';

const DomainsLibraryModal = ({ onClose, onInsert, modelType }) => {
  const [domains, setDomains] = useState([]);
  const [filteredDomains, setFilteredDomains] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all'); // 'all', 'table', 'field'
  const [filterModelType, setFilterModelType] = useState('all'); // 'all', 'relational', 'analytical', 'nosql'
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [renameDomain, setRenameDomain] = useState(null);

  useEffect(() => {
    loadDomains();
  }, []);

  useEffect(() => {
    filterDomains();
  }, [domains, searchQuery, filterType, filterModelType]);

  const loadDomains = async () => {
    setIsLoading(true);
    try {
      // Load all domains globally (no modelType filter)
      // Domains are now shared across all model types
      const result = await domainApi.getAll();
      setDomains(result);
    } catch (error) {
      console.error('Error loading domains:', error);
      toast.error('Failed to load domains');
    } finally {
      setIsLoading(false);
    }
  };

  const filterDomains = () => {
    let filtered = domains;

    // Filter by domain type (table/field)
    if (filterType !== 'all') {
      filtered = filtered.filter(d => d.type === filterType);
    }

    // Filter by model type (relational/analytical/nosql)
    if (filterModelType !== 'all') {
      filtered = filtered.filter(d => d.modelType === filterModelType);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(d => 
        d.name.toLowerCase().includes(query) ||
        d.description?.toLowerCase().includes(query)
      );
    }

    setFilteredDomains(filtered);
  };

  const handleInsert = (domain) => {
    onInsert(domain);
    onClose();
  };

  const handleRename = async (newName) => {
    if (!renameDomain) return;
    
    try {
      const updated = await domainApi.rename(renameDomain._id, newName);
      setDomains(domains.map(d => d._id === renameDomain._id ? updated : d));
      toast.success('Domain renamed successfully');
    } catch (error) {
      console.error('Error renaming domain:', error);
      toast.error('Failed to rename domain');
      throw error;
    }
  };

  const handleDelete = async (domainId) => {
    try {
      await domainApi.delete(domainId);
      setDomains(domains.filter(d => d._id !== domainId));
      setDeleteConfirm(null);
      toast.success('Domain deleted successfully');
    } catch (error) {
      console.error('Error deleting domain:', error);
      toast.error('Failed to delete domain');
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Objects Library
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Global reusable templates • Shared across all models
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-700 dark:text-gray-200"
          >
            <X size={20} />
          </button>
        </div>

        {/* Filters */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 space-y-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Search domains by name or description..."
            />
          </div>

          {/* Domain Type Filter (Table/Field) */}
          <div className="flex gap-2">
            <button
              onClick={() => setFilterType('all')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filterType === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              All ({domains.length})
            </button>
            <button
              onClick={() => setFilterType('table')}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                filterType === 'table'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              <Database size={16} />
              Tables ({domains.filter(d => d.type === 'table').length})
            </button>
            <button
              onClick={() => setFilterType('field')}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                filterType === 'field'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              <Columns size={16} />
              Fields ({domains.filter(d => d.type === 'field').length})
            </button>
          </div>

          {/* Model Type Filter */}
          <div className="flex gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center">
              Model Type:
            </span>
            <button
              onClick={() => setFilterModelType('all')}
              className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                filterModelType === 'all'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilterModelType('relational')}
              className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                filterModelType === 'relational'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Relational ({domains.filter(d => d.modelType === 'relational').length})
            </button>
            <button
              onClick={() => setFilterModelType('analytical')}
              className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                filterModelType === 'analytical'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Analytical ({domains.filter(d => d.modelType === 'analytical').length})
            </button>
            <button
              onClick={() => setFilterModelType('nosql')}
              className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                filterModelType === 'nosql'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              NoSQL ({domains.filter(d => d.modelType === 'nosql').length})
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500 dark:text-gray-400">Loading domains...</div>
            </div>
          ) : filteredDomains.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-gray-400">
              <Database size={48} className="mb-4 opacity-50" />
              <p className="text-lg font-medium">No domains found</p>
              <p className="text-sm mt-1">
                {searchQuery || filterType !== 'all' 
                  ? 'Try adjusting your filters' 
                  : 'Save your first object to get started'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredDomains.map((domain) => (
                <div
                  key={domain._id}
                  className="border-2 border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-blue-500 dark:hover:border-blue-400 transition-all"
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start gap-2 flex-1">
                      {domain.type === 'table' ? (
                        <Database size={20} className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                      ) : (
                        <Columns size={20} className="text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                          {domain.name}
                        </h3>
                        {domain.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                            {domain.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="space-y-2 mb-3">
                    {/* Model Type Badge */}
                    <div className="flex items-center gap-2">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                        domain.modelType === 'relational' 
                          ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400'
                          : domain.modelType === 'analytical'
                          ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-400'
                          : 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400'
                      }`}>
                        {domain.modelType === 'relational' ? 'Relational' : domain.modelType === 'analytical' ? 'Analytical' : 'NoSQL'}
                      </span>
                      {domain.nodeType && (
                        <span className="text-xs text-gray-500 dark:text-gray-500 capitalize">
                          ({domain.nodeType})
                        </span>
                      )}
                    </div>

                    {domain.type === 'table' && (
                      <>
                        <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                          <Columns size={14} />
                          <span>{domain.fields?.length || 0} fields</span>
                        </div>
                        {domain.tags && domain.tags.length > 0 && (
                          <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                            <Tag size={14} />
                            <span>{domain.tags.length} tags</span>
                          </div>
                        )}
                      </>
                    )}
                    {domain.type === 'field' && domain.field && (
                      <div className="text-xs space-y-1">
                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                          <span>Type:</span>
                          <span className="font-mono bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                            {domain.field.type}
                          </span>
                        </div>
                        {domain.field.isPrimaryKey && (
                          <span className="inline-block px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400 rounded text-xs">
                            Primary Key
                          </span>
                        )}
                      </div>
                    )}
                    <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-500">
                      <Calendar size={12} />
                      <span>{formatDate(domain.createdAt)}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleInsert(domain)}
                      className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 text-sm"
                    >
                      <Plus size={14} />
                      <span>Insert</span>
                    </button>
                    <button
                      onClick={() => setRenameDomain(domain)}
                      className="px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                      title="Rename domain"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(domain._id)}
                      className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                      title="Delete domain"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>

      {/* Rename Modal */}
      <RenameModal
        isOpen={!!renameDomain}
        currentName={renameDomain?.name || ''}
        itemType="Domain"
        onClose={() => setRenameDomain(null)}
        onRename={handleRename}
      />

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Delete Domain?
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete this domain? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DomainsLibraryModal;
